import { AxiosRequestConfig } from 'axios';
import instance from 'api';
import { IDynamicTableParams } from 'components/DynamicTable';
import { IAvailableColumnModel, IAvailableGroupColumnModel, IOrderColumnModel, IRealEstateModel } from 'interfaces';
import {
  IArea,
  IChecklistApplication,
  IDefectType,
  IGpr,
  IGprListRow,
  IGprLoadDatesRequest,
  IGprLoadDay,
  IGprLoadPreviewRequest,
  IGprPerimeterObjectPage,
  IGprPerimeterObjectsRequest,
  IGprPerimeterPreview,
  IGprPerimeterPreviewRequest,
  IGprPerimeterTemplate,
  IGprPerimeterTemplateSave,
  IGprRoomModel,
  IGprRunCard,
  IGprRunListRow,
  IGprRunRollbackResult,
  IGprRunSummary,
  IGprSave,
  IGprWorkType,
  IObjectType,
  IRealEstateClass,
  IServiceOrg,
} from './interfaces';

// График (ГПР)
export const getGprList = (data: IDynamicTableParams) => {
  return instance()
    .post<IGprListRow[]>('/gpr/list', data)
    .then((response) => response.data);
};

export const getGprAvailableColumns = () => {
  return instance()
    .post<IAvailableColumnModel[]>('/gpr/availableColumns')
    .then((response) => response.data);
};

export const getGprAvailableGroupColumns = () => {
  return instance()
    .post<IAvailableGroupColumnModel[]>('/gpr/availableGroupColumns')
    .then((response) => response.data);
};

export const loadGpr = (gprId: number) => {
  return instance()
    .get<IGpr>(`/gpr/load/${gprId}`)
    .then((response) => response.data);
};

// Сортировка списка шаблонов: ключ направления в OrderColumn называется sortType, неизвестное
// поле Spring молча выбрасывает — и порядок уходил бы в DESC.
const WORK_TEMPLATE_ORDER_COLUMNS: IOrderColumnModel[] = [{ columnName: 'gprName', sortType: 'ASC' }];

// Шаблон работы (SERVICE-5298) — это уже созданный график: своей таблицы шаблонов нет.
export const getWorkTemplates = () => {
  return instance()
    .post<IGprListRow[]>('/gpr/list', {
      pageNo: 1,
      pageSize: 500,
      selectColumns: ['gprId', 'gprName', 'workTypeName', 'defectName'],
      filterColumns: [],
      orderColumns: WORK_TEMPLATE_ORDER_COLUMNS,
    })
    .then((response) => response.data);
};

export const createGpr = (gpr: IGprSave) => {
  return instance()
    .post<number>('/gpr/save', gpr)
    .then((response) => response.data);
};

export const updateGpr = (gprId: number, gpr: IGprSave) => {
  return instance()
    .post<void>(`/gpr/${gprId}/update`, gpr)
    .then((response) => response.data);
};

export const activateGpr = (gprId: number) => {
  return instance()
    .post<void>(`/gpr/${gprId}/activate`)
    .then((response) => response.data);
};

export const deactivateGpr = (gprId: number) => {
  return instance()
    .post<void>(`/gpr/${gprId}/deactivate`)
    .then((response) => response.data);
};

// Периметр графика
export const previewPerimeter = (request: IGprPerimeterPreviewRequest) => {
  return instance()
    .post<IGprPerimeterPreview>('/gpr/perimeter/preview', request)
    .then((response) => response.data);
};

// Страница списка объектов периметра — превью отдаёт только счётчики. config нужен для
// cancelToken: перезапрос при смене периметра или поиска не должен дать устаревшему ответу
// перезаписать актуальную страницу.
export const getPerimeterObjects = (request: IGprPerimeterObjectsRequest, config?: AxiosRequestConfig) => {
  return instance()
    .post<IGprPerimeterObjectPage>('/gpr/perimeter/objects', request, config)
    .then((response) => response.data);
};

export const getPerimeterTemplates = () => {
  return instance()
    .post<IGprPerimeterTemplate[]>('/gpr/perimeter/template/list')
    .then((response) => response.data);
};

export const savePerimeterTemplate = (request: IGprPerimeterTemplateSave) => {
  return instance()
    .post<number>('/gpr/perimeter/template/save', request)
    .then((response) => response.data);
};

export const deletePerimeterTemplate = (templateId: number) => {
  return instance()
    .post<void>(`/gpr/perimeter/template/delete/${templateId}`)
    .then((response) => response.data);
};

// Проверка нагрузки на исполнителей (SERVICE-5298) — префикс /gpr/workload, а не /gpr/load:
// под /gpr/load/{id} живёт загрузка графика по id, это другое понятие.
// Ближайшие даты запуска черновика (до 5) — вкладки модалки; у событийного графика их нет вовсе.
export const getLoadDates = (request: IGprLoadDatesRequest) => {
  return instance()
    .post<string[]>('/gpr/workload/dates', request)
    .then((response) => response.data);
};

// Нагрузка ровно одной даты: расчёт по чужим графикам дорогой, поэтому все даты сразу не тянем.
export const getLoadPreview = (request: IGprLoadPreviewRequest) => {
  return instance()
    .post<IGprLoadDay>('/gpr/workload/preview', request)
    .then((response) => response.data);
};

// Реестр запусков. Строка ответа — IGprRunListRow (только зарегистрированные в
// GprRunRepository колонки), а не полный IGprRun карточки запуска.
export const getGprRunList = (data: IDynamicTableParams) => {
  return instance()
    .post<IGprRunListRow[]>('/gpr/run/list', data)
    .then((response) => response.data);
};

// Сводка плиток реестра запусков — то же тело, что уходит в /gpr/run/list.
export const getGprRunSummary = (data: IDynamicTableParams) => {
  return instance()
    .post<IGprRunSummary>('/gpr/run/summary', data)
    .then((response) => response.data);
};

export const getGprRunAvailableColumns = () => {
  return instance()
    .post<IAvailableColumnModel[]>('/gpr/run/availableColumns')
    .then((response) => response.data);
};

export const getGprRunAvailableGroupColumns = () => {
  return instance()
    .post<IAvailableGroupColumnModel[]>('/gpr/run/availableGroupColumns')
    .then((response) => response.data);
};

export const loadGprRun = (runId: number) => {
  return instance()
    .get<IGprRunCard>(`/gpr/run/load/${runId}`)
    .then((response) => response.data);
};

// Отчёт по одному запуску — «Выгрузить» в реестре и «Отчёт по шаблону» в карточке. Отдаётся PDF
// по шаблону заказчика: Excel-выгрузка (/gpr/run/{id}/excel) осталась на бэкенде, но из интерфейса
// больше не вызывается — в ней всего 7 колонок из 15.
export const getGprRunPdf = (runId: number) => {
  return instance()
    .get<Blob>(`/gpr/run/${runId}/pdf`, { responseType: 'blob' })
    .then((response) => response.data);
};

export const rollbackGprRun = (runId: number) => {
  return instance()
    .post<IGprRunRollbackResult>(`/gpr/run/${runId}/rollback`)
    .then((response) => response.data);
};

// Справочники для формы графика
export const getGprWorkTypes = () => {
  return instance()
    .post<IGprWorkType[]>('/techMap/workType/listAllNotDeleted')
    .then((response) => response.data);
};

export const getDefectTypes = () => {
  return instance()
    .post<IDefectType[]>('/dictionary/defects', {})
    .then((response) => response.data);
};

export const getChecklistApplicationListAllNotDeleted = () => {
  return instance()
    .post<IChecklistApplication[]>('/checkListApplication/listAllNotDeleted')
    .then((response) => response.data);
};

export const getServiceOrgs = () => {
  return instance()
    .post<IServiceOrg[]>('/dictionary/serviceOrgsParams', {})
    .then((response) => response.data);
};

export const getAreas = () => {
  return instance()
    .post<IArea[]>('/area/list', {})
    .then((response) => response.data);
};

export const getRealEstateClasses = () => {
  return instance()
    .get<IRealEstateClass[]>('/calculatorTariff/realEstateClass/search', { params: { search: '' } })
    .then((response) => response.data);
};

export const getObjectTypes = () => {
  return instance()
    .post<IObjectType[]>('/realEstateObjectType/listAllNotDeleted')
    .then((response) => response.data);
};

// Системное время создания заявок (шаг «Расписание», задача 9) — тот же ключ app_settings, что
// у старого движка Тех. карты (CREATE_APPLICATION_TECHMAP_CRON). Через /loadByKey, а не
// /{key}/load — у второго есть проверка APP_SETTINGS_MANAGER, которой у менеджера ГПР нет.
// Своего значения по умолчанию фронт не придумывает: время задаётся в админке, а если настройки
// нет или запрос не прошёл — показываем прочерк. Форма грузится через Loadable.Map, поэтому
// запрос не должен её ронять.
export const getSystemRunTime = () => {
  return instance()
    .post<{ value: string }>('/appSettings/CREATE_APPLICATION_TECHMAP_CRON/loadByKey')
    .then((response) => response.data.value)
    .catch(() => '');
};

// Помещения для формы ГПР: /dictionary/rooms джойнит собственников и теряет помещения
// «Перспективных» объектов, а запуск их развернёт — счётчик заявок расходился.
export const getGprRooms = (realEstateGuid: string) => {
  return instance()
    .post<IGprRoomModel[]>('/gpr/rooms', { realEstateGuid })
    .then((response) => response.data);
};

// Нерабочие дни — тот же источник, которым бэкенд переносит заявки (производственный
// календарь, а при отсутствии строки — воскресенье). Субботу сами не додумываем.
// Ошибку глотаем, как getSystemRunTime: подсветка нерабочих дней — украшение, без неё форма
// полностью работоспособна, а эндпоинт новый — в окне между выкаткой фронта и бэкенда тост
// об ошибке увидел бы каждый, кто открыл форму графика.
export const getDaysOff = (dateFrom: string, dateTo: string) => {
  return instance()
    .post<string[]>('/gpr/calendar/daysOff', { dateFrom, dateTo })
    .then((response) => response.data)
    .catch((): string[] => []);
};

// Поиск объекта для селекта «Объект» шага «Периметр объектов» — каскадируется от выбранных
// участков (areaIds); serviceOrgGuids v2-эндпоинт не принимает.
// realEstateGuids — точечная догрузка конкретных объектов по guid (например, имена уже выбранных
// объектов при открытии сохранённого графика), без ограничения по области.
// includeUnserved — только для точечной догрузки по guid: объект группы «Ушедшие» больше не
// обслуживается, и без этого флага (default false на бэкенде, RealEstateV2ListRequest) он не
// найдётся вовсе, а в чипе останется голый guid вместо имени.
export const getRealEstatesShort = (data: {
  areaIds?: number[];
  name?: string;
  realEstateGuids?: string[];
  includeUnserved?: boolean;
}) => {
  return instance()
    .post<IRealEstateModel[]>('/dictionary/v2/realEstatesShort', { ...data, nameWithCity: true, searchWithCity: true })
    .then((response) => response.data);
};
